import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import MemberPortalGuard from "./MemberPortalGuard";
import { getMemberPaymentSessionStatus } from "../../api/memberPortalClient";
import { PageHeader, SectionCard } from "./components/MemberUI";

function resolveOutcome(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "completed" || normalized === "success") {
    return "success";
  }
  if (normalized === "failed" || normalized === "expired" || normalized === "error") {
    return "error";
  }
  if (normalized === "redirected" || normalized === "initiated" || normalized === "pending") {
    return "pending";
  }
  return "unknown";
}

function MemberPaymentResultContent() {
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const invoiceIdParam = searchParams.get("invoiceId");
  const sessionIdParam = searchParams.get("sessionId");

  const [loading, setLoading] = useState(Boolean(sessionIdParam && !invoiceIdParam));
  const [invoiceId, setInvoiceId] = useState(invoiceIdParam || "");
  const [sessionStatus, setSessionStatus] = useState(statusParam || "");
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!sessionIdParam || invoiceIdParam) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { ok, body } = await getMemberPaymentSessionStatus(sessionIdParam);
        if (cancelled) return;
        if (!ok) {
          setLoadError(body?.error?.message || "Could not load payment details.");
          return;
        }
        if (body.data?.invoice_id) {
          setInvoiceId(String(body.data.invoice_id));
        }
        if (!statusParam && body.data?.status) {
          setSessionStatus(body.data.status);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message || "Could not load payment details.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionIdParam, invoiceIdParam, statusParam]);

  const outcome = resolveOutcome(statusParam || sessionStatus);

  if (loading) {
    return (
      <div className="p-4 md:p-6 flex justify-center items-center min-h-[50vh]">
        <CircularProgress sx={{ color: "#D71920" }} />
      </div>
    );
  }

  const title =
    outcome === "success"
      ? "Payment successful"
      : outcome === "pending"
        ? "Payment in progress"
        : outcome === "error"
          ? "Payment not completed"
          : "Payment result";

  const description =
    outcome === "success"
      ? "Your online payment was processed successfully. Your invoice will reflect the updated balance shortly."
      : outcome === "pending"
        ? "Your payment is still being processed. Please check your invoice again in a few moments."
        : outcome === "error"
          ? "The payment could not be completed. You can try again from your invoice or pay at the club."
          : loadError ||
            "We could not determine the outcome of your payment. Please check your invoice or contact your club.";

  const Icon =
    outcome === "success"
      ? CheckCircleOutlineIcon
      : outcome === "pending"
        ? HourglassEmptyIcon
        : ErrorOutlineIcon;

  const iconColor =
    outcome === "success" ? "#059669" : outcome === "pending" ? "#F7931E" : "#D71920";

  return (
    <div className="p-4 md:p-6 space-y-5 bg-gradient-to-b from-gx-canvas to-gx-surface min-h-full">
      <PageHeader
        title="Payment result"
        subtitle="Online payment via secure gateway"
      />

      <SectionCard>
        <div className="flex flex-col items-center text-center py-6 px-2">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: `${iconColor}15`, color: iconColor }}
          >
            <Icon sx={{ fontSize: 40 }} />
          </div>
          <h2 className="text-xl font-bold text-gx-ink">{title}</h2>
          <p className="text-sm text-gx-ink-soft mt-2 max-w-md">{description}</p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {invoiceId ? (
              <Link
                to={`/member/invoices/${invoiceId}`}
                className="px-5 py-2.5 rounded-full bg-gx-red text-white text-sm font-semibold hover:bg-[#b5151b] transition"
              >
                View invoice
              </Link>
            ) : null}
            <Link
              to="/member/invoices"
              className="px-5 py-2.5 rounded-full border border-gx-border bg-white text-gx-ink text-sm font-semibold hover:border-gx-red hover:text-gx-red transition"
            >
              All invoices
            </Link>
            <Link
              to="/member/dashboard"
              className="px-5 py-2.5 rounded-full border border-gx-border bg-white text-gx-ink text-sm font-semibold hover:border-gx-red hover:text-gx-red transition"
            >
              Dashboard
            </Link>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

function MemberPaymentResult() {
  return (
    <MemberPortalGuard>
      <MemberPaymentResultContent />
    </MemberPortalGuard>
  );
}

export default MemberPaymentResult;
